import { allComposables, ComposedValue } from "../composables";
import { Event } from "../contracts";
import { observable, action, IObservableArray } from "mobx";

/**
 * Piper class - deals with pipes and managing them.
 */
/* istanbul ignore next */
export class Piper {
    @observable pipeline: IObservableArray<ComposedValue>

    constructor(composables: string[]) {
        this.pipeline = this.compilePipeline(composables);
    }

    /**
     * executePipeline - runs a dataset through all the elements of the pipe
     * @param {Event[]} data data to manipulate
     * @returns {Event[]} manipulated data
     *
     * TODO: decide if to work with reference data or to force .slice() on the input array.
     */
    @action.bound executePipeline(data: IObservableArray<Event> | Event[]) {
        return this.pipeline.reduce((a: IObservableArray<Event> | Event[], b) => a[b.method](b.cb), data);
    }

    /**
     * compilePipeline - creates array of the different methods that need to run in the provided sequence
     * @param {STRING[]} composables names of the entities that are going to be piped
     * @returns {ComposedValue[]}
     */
    @action.bound private compilePipeline(composables) {
        return this.pipeline = composables
            .map(x => allComposables[x])
            .filter(x => typeof x !== "undefined");
    }

    /**
     * swap - removes one entity and puts another in it's place if both exist.
     * @param {STRING} toSwap name of entity you want to swap
     * @param {STRING} swapWith name of entity to swap with
     * @returns {VOID}
     */
    @action.bound swap(toSwap: string, swapWith: string): void {
        let i = this.pipeline.findIndex(x => x.name === toSwap);
        if (i > -1 && typeof allComposables[swapWith] !== "undefined") {
            this.pipeline.splice(i, 1, allComposables[swapWith])
        }
    }

    /**
     * remove - removes element from the pipe.
     * @param {STRING} toRemove name of entity to remove
     * @returns {VOID}
     */
    @action.bound remove(toRemove: string): void {
        let i = this.pipeline.findIndex(x => x.name === toRemove);
        if (i > -1) {
            this.pipeline.splice(i, 1)
        }
    }

    /**
     * add - adds entity on a specified index or at the end of the array. If it already exists it does nothing.
     * @param {STRING} toAdd name of entity to add
     * @param {NUMBER} index optional index where to put the new element. Defaults to append at the end.
     */
    @action.bound add(toAdd: string, index?: number): void {
        let i = this.pipeline.findIndex(x => x.name === toAdd);
        if (i === -1 && typeof allComposables[toAdd] !== "undefined") {
            this.pipeline.splice(index || this.pipeline.length, 0, allComposables[toAdd])
        }
    }

    /**
     * append - alis for .add()
     * @param {STRING} toAdd name of entity to add at the end of the array.
     */
    append(toAdd: string): void {
        this.add(toAdd);
    }

    /**
     * appendAll - .add() to all elements of the array
     * @param {STRING[]} toAdd
     */
    appendAll(toAdd: string[]): void {
        toAdd.forEach((elementToAdd) => this.add(elementToAdd));
    }
}